import { Component, forwardRef, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator } from '@angular/forms';
import { debounce, noop } from 'lodash';

import { EMMAFormElementComponent } from './emma-form-element.component';

export interface MultiSliderConfig {
  label: string;
  value: number;
  color: string;
  id?: any;
}

const EMMA_SLIDER_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  // tslint:disable-next-line: no-use-before-declare
  useExisting: forwardRef(() => EMMAMultiSliderComponent),
  multi: true,
};

const EMMA_SLIDER_VALIDATOR: any = {
  provide: NG_VALIDATORS,
  // tslint:disable-next-line: no-use-before-declare
  useExisting: forwardRef(() => EMMAMultiSliderComponent),
  multi: true,
};

@Component({
    selector: 'emma-multi-slider',
    templateUrl: './emma-multi-slider.component.html',
    providers: [
        EMMA_SLIDER_CONTROL_VALUE_ACCESSOR,
        EMMA_SLIDER_VALIDATOR,
        {
            provide: EMMAFormElementComponent,
            useExisting: EMMAMultiSliderComponent,
        },
    ],
    standalone: false
})
export class EMMAMultiSliderComponent
  extends EMMAFormElementComponent
  implements OnChanges, ControlValueAccessor, Validator
{
  @Input() unit = '';
  @Input() min = 0;
  @Input() max = 100;
  @Input() step = 1;
  @Input() minRange = 1;
  @Input() showTicks = false;
  @Input() showTicksValues = false;
  @Input() tickStep = 10;
  @Input() noSwitching = true;
  @Input() hidePointerLabels = true;
  @Input() showSelectionBar = true;
  @Input() draggableRange = false;
  @Input() numberAsTime = false;
  @Input() hideLimitLabels = false;
  @Input() firstLimitLabelsOnly = false;
  @Input() sliders: MultiSliderConfig[] = [];
  sliderValues: number[] = [];
  isInteger = true;

  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: any) => void = noop;

  onChangeValue = debounce((index: number, newValue: number): void => {
    if (this.sliderValues[index] !== newValue) {
      this.sliderValues[index] = newValue;
      this.updateValues(true);
    }
  }, 100);

  onChangePrevValue = debounce((index: number, newValue: number): void => {
    const prev = index - 1;
    if (this.sliders.length > prev) {
      if (this.sliderValues[prev] !== newValue) {
        this.sliderValues[prev] = newValue;
        this.updateValues(false);
      }
    }
  }, 100);

  getPrevValue(index: number): number {
    return this.sliderValues[index - 1] || 0;
  }
  getValue(index: number): number {
    return this.sliderValues[index] || 0;
  }
  getValues(): number[] {
    return this.sliders.map((p) => p.value);
  }
  private updateTotals() {
    const l = this.sliders.length;
    for (let i = 0; i < l; ++i) {
      if (i === l - 1) {
        // Grupo de control
        this.sliders[i].value = this.max - this.sliderValues[i - 1];
      } else if (i) {
        // Test AB
        this.sliders[i].value = this.sliderValues[i] - this.sliderValues[i - 1];
      } else {
        // Original
        this.sliders[i].value = this.sliderValues[i];
      }
      if (this.isInteger) {
        this.sliders[i].value = Math.round(this.sliders[i].value);
      }
    }
  }
  private solveSync(backwards: boolean) {
    const len = this.sliderValues.length;
    if (backwards) {
      for (let i = 1; i < len; ++i) {
        if (this.sliderValues[i - 1] > this.sliderValues[i]) {
          this.sliderValues[i] = this.sliderValues[i - 1];
        }
      }
    } else {
      for (let i = len - 1; i > 0; --i) {
        if (this.sliderValues[i - 1] > this.sliderValues[i]) {
          this.sliderValues[i - 1] = this.sliderValues[i];
        }
      }
    }
  }
  private updateSliderValues() {
    this.sliderValues = [];
    let acc = 0;
    for (const slider of this.sliders) {
      const value = (slider.value || 0) + acc;
      this.sliderValues.push(value);
      acc = value;
    }
  }

  private updateValues(backwards: boolean) {
    this.solveSync(backwards);
    this.updateTotals();
    this.onTouchedCallback();
    this.onChangeCallback(this.sliders);
    this.$change.emit();
  }

  override ngOnChanges(changes: SimpleChanges): void {
    if ('step' in changes) {
      this.isInteger = this.step % 1 === 0;
    }
    if ('sliders' in changes) {
      this.updateSliderValues();
    }
  }

  // From ControlValueAccessor interface
  writeValue(sliders: MultiSliderConfig[] | null): void {
    if (sliders && sliders !== this.sliders) {
      this.sliders = sliders;
      this.updateSliderValues();
    }
  }

  // From ControlValueAccessor interface
  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  // From ControlValueAccessor interface
  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }
}
